import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useRouter, useSearchParams } from 'next/navigation';

import BottomSheetModalWrapper from '@Components/modals/modal-wrapper/bottom-sheet-modal-wrapper';
import { Button } from '@app/shadcn/components/ui/button';
import { useToast } from '@app/shadcn/components/ui/use-toast';

import RegexCard from '@app/components/cards/regex-card';
import GroupInfo from '@app/components/group/group-info';
import GroupMember from '@app/components/group/group-member';
import { useModal } from '@app/components/modal-views/context';
import { buttonConstant } from '@app/constants/locales/button';
import { groupConstant } from '@app/constants/locales/group';
import { toastMessage } from '@app/constants/locales/toast-message';
import { useGroupForm } from '@app/lib/hooks/use-group-form';
import { GroupInfoDto } from '@app/models/dtos/groups';
import { WorkspaceDto } from '@app/models/dtos/workspace-dto';
import { handleRegexType } from '@app/models/enums/group-regex';
import { selectForm } from '@app/store/forms/slice';
import { useAppSelector } from '@app/store/hooks';
import { useCreateRespondersGroupMutation } from '@app/store/workspaces/api';
import { selectWorkspace } from '@app/store/workspaces/slice';
import { useBottomSheetModal } from '../contexts/bottom-sheet-modal-context';

export default function CreateGroupModal() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const { toast } = useToast();
    let formId: string = (searchParams?.get('formId') as string) ?? '';
    const { closeBottomSheetModal } = useBottomSheetModal();
    const { t } = useTranslation();
    const { closeModal } = useModal();
    const workspace: WorkspaceDto = useAppSelector(selectWorkspace);
    const form = useAppSelector(selectForm);
    const { addFormOnGroup } = useGroupForm();
    const existingGroups = form?.groups || [];
    const [groupInfo, setGroupInfo] = useState<GroupInfoDto>({
        name: '',
        description: '',
        emails: [],
        regex: '',
        formId: formId ?? ''
    });
    const [createResponderGroup, { isLoading }] = useCreateRespondersGroupMutation();
    const handleInput = (event: any) => {
        setGroupInfo({
            ...groupInfo,
            [event.target.id]: event.target.value
        });
    };

    const handleRegex = (regex: string, type: handleRegexType) => {
        if (type === handleRegexType.ADD) {
            setGroupInfo({
                ...groupInfo,
                regex
            });
            closeModal();
        } else if (type === handleRegexType.REMOVE) {
            setGroupInfo({
                ...groupInfo,
                regex: ''
            });
        }
    };

    const handleCreateGroup = async () => {
        try {
            await createResponderGroup({
                groupInfo: groupInfo,
                workspace_id: workspace.id
            }).then((response: any) => {
                if ('data' in response) {
                    toast({ description: t(toastMessage.workspaceSuccess).toString() });
                    addFormOnGroup({
                        groups: [],
                        groupsForUpdate: [...existingGroups, { ...response.data, id: response.data._id }],
                        form,
                        workspaceId: workspace.id
                    });
                    closeBottomSheetModal();
                } else
                    toast({
                        description: t(toastMessage.somethingWentWrong).toString(),
                        variant: 'destructive'
                    });
            });
        } catch (error) {
            toast({ description: t(toastMessage.somethingWentWrong).toString(), variant: 'destructive' });
        }
    };

    const handleAddMembers = (members: Array<string>) => {
        if (groupInfo.emails) {
            setGroupInfo({
                ...groupInfo,
                emails: [...groupInfo.emails, ...members]
            });
            closeModal();
        } else {
            toast({ description: t(toastMessage.somethingWentWrong).toString(), variant: 'destructive' });
        }
    };

    const handleRemoveMember = (email: string) => {
        setGroupInfo({
            ...groupInfo,
            emails: groupInfo.emails?.filter((groupInfoEmail) => groupInfoEmail !== email)
        });
    };

    return (
        <>
            <BottomSheetModalWrapper>
                <div>
                    <div className="h2-new mb-2">{t(groupConstant.createGroup)}</div>
                    <div className="p2-new text-black-700">{t('GROUP.TITLE')}</div>
                </div>
                <div className="mb-4 flex flex-col md:max-w-[700px] xl:max-w-[1000px]">
                    <div className="md:max-w-[618px]">
                        <div className="flex flex-col gap-12 pt-16">
                            <GroupInfo handleInput={handleInput} groupInfo={groupInfo} />
                            <div>
                                <RegexCard handleRegex={handleRegex} regex={groupInfo.regex} />
                                {groupInfo.emails && <GroupMember emails={groupInfo.emails} handleAddMembers={handleAddMembers} handleRemoveMember={handleRemoveMember} />}
                            </div>
                            <div>
                                <Button
                                    data-umami-event={'Add New Group Button'}
                                   
                                    isLoading={isLoading}
                                    variant="secondary"
                                    disabled={!groupInfo.name || (groupInfo.emails?.length === 0 && groupInfo.regex?.length === 0)}
                                    onClick={handleCreateGroup}
                                >
                                    {t(buttonConstant.saveGroup)}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </BottomSheetModalWrapper>
        </>
    );
}
